import { ref, watch } from 'vue';
import { MANDATORY_CHARTS } from '@/config/chartConfig';

export interface ChartVisibilityPreferences {
    // Lista ordinata di ID (entità sensori o ID grafici combinati)
    orderedCharts: string[];

    // MultiChartsPage sections (Static Devices) - Keep for compatibility
    environmentSection: boolean;
    singleSensorSection: boolean;
    combinedChartsSection: boolean;

    // TelemetryPage sections (Dynamic Devices)
    locationCard: boolean;
    vehicleStatus: boolean;
    historicalChart: boolean;
    routeMap: boolean;
    environmentSectionVehicle: boolean;
    statisticsPanel: boolean;
}

const STORAGE_KEY = 'archimede_chart_visibility';

const defaultPreferences: ChartVisibilityPreferences = {
    // Default: i 3 obbligatori iniziali
    orderedCharts: [...MANDATORY_CHARTS],

    // Altri default (compatibilità)
    environmentSection: true,
    singleSensorSection: true,
    combinedChartsSection: true,
    locationCard: true,
    vehicleStatus: true,
    historicalChart: true,
    routeMap: true,
    environmentSectionVehicle: true,
    statisticsPanel: true,
};

// Carica le preferenze da localStorage
function loadPreferences(): ChartVisibilityPreferences {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            const prefs = { ...defaultPreferences, ...parsed };

            // Garantisci che i mandatory ci siano sempre (anche se rimosso accidentalmente in versioni precedenti)
            MANDATORY_CHARTS.forEach(chartId => {
                if (!prefs.orderedCharts.includes(chartId)) {
                    prefs.orderedCharts.unshift(chartId); // Aggiungi in cima se manca
                }
            });

            return prefs;
        }
    } catch (e) {
        console.warn('Failed to load chart visibility preferences:', e);
    }
    return { ...defaultPreferences };
}

// Salva le preferenze in localStorage
function savePreferences(prefs: ChartVisibilityPreferences): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch (e) {
        console.warn('Failed to save chart visibility preferences:', e);
    }
}

const globalPreferences = ref<ChartVisibilityPreferences>(loadPreferences());
const globalTemporaryChart = ref<string | null>(null);

// Auto-save quando le preferenze cambiano
watch(
    globalPreferences,
    (newPrefs) => {
        savePreferences(newPrefs);
    },
    { deep: true }
);

export function useChartVisibility() {
    const preferences = globalPreferences;

    // Ripristina i default
    const resetToDefaults = () => {
        preferences.value = { ...defaultPreferences };
    };

    // Aggiungi un grafico
    const addChart = (chartId: string) => {
        if (!preferences.value.orderedCharts.includes(chartId)) {
            preferences.value.orderedCharts.push(chartId);
        }
    };

    // Rimuovi un grafico (se non è obbligatorio)
    const removeChart = (chartId: string) => {
        if (MANDATORY_CHARTS.includes(chartId)) return;
        preferences.value.orderedCharts = preferences.value.orderedCharts.filter(id => id !== chartId);
    };

    // Cambia posizione
    const moveChart = (index: number, direction: 'up' | 'down') => {
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= preferences.value.orderedCharts.length) return;

        const list = [...preferences.value.orderedCharts];
        const temp = list[index];
        list[index] = list[newIndex];
        list[newIndex] = temp;

        preferences.value.orderedCharts = list;
    };

    const setTemporaryChart = (chartId: string) => {
        globalTemporaryChart.value = chartId;
    };

    const clearTemporaryChart = () => {
        globalTemporaryChart.value = null;
    };

    return {
        preferences,
        resetToDefaults,
        addChart,
        removeChart,
        moveChart,
        temporaryChart: globalTemporaryChart,
        setTemporaryChart,
        clearTemporaryChart,
    };
}
